import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
//import type { ButtonTheme } from '../../../../ui/components';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { AddParticipantStructure, TeamData, ParticipantDataStructure } from '../../../admin/teams/teamsListConfig';
import CampaignService from '../../../../sdk/services/campaignService';
import { initTeamForm } from '../../../admin/teams/edit/[teamId]';
import {
  LoggerService,
  PAGES,
  removeState,
  trackPage,
  processProfilePictureData,
  convertGMT8date,
} from '../../../../ui/utils';
import {
  //AdminPageHeader,
  Button,
  ButtonSize,
  ButtonStyles,
  Input,
  InputValidations,
  //DashboardPageHeaderMobile,
  MLPVersion,
  PhoneNumber,
  Modal,
  Checkbox,
  Tag,
  TagStyle,
  Theme,
  //useBreakpoint
} from '../../../../ui/components';
import { customEventPropType } from '../../../../ui/components/ImageEditor/types/propsType';
import ImageEditor from '../../../../ui/components/ImageEditor/ImageEditor';
import { useAPICall, useAppSelector } from '../../../../hooks';
import ParticipantService from '../../../../sdk/services/participantService';
import AttachmentService from '../../../../sdk/services/attachmentService';
import TeamService from '../../../../sdk/services/teamService';
import MDMSService from '../../../../sdk/services/mdmsService';
import { DynamicObjectInterface } from '../../../../sdk/type';
import { DropDownData } from '../../../../ui/components/Form/DropDown/types/propsType';
import { countryCodeNumber } from '../../../../__mockData/phoneNumber';
import { isValidEmail } from '../../../../ui/utils/common';
import ValidationService from '../../../../sdk/services/validationService';
import { CampaignConfigDataOrder, OrderData } from '../../../admin/teams/edit/add-participant/[teamId]';
import ChangeButton from '../../../../assets/image/icons/change_button.svg';
import RemoveButton from '../../../../assets/image/icons/remove_button.svg';
import personalizeImg from '../../../../assets/image/ProfileImageUpload.svg';
import styles from './index.module.scss';

interface JoinTeamProps {
  decodedData: DynamicObjectInterface;
}

export const JoinTeam = (props: JoinTeamProps) => {
  const { decodedData } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminToast } = useAdminToast();
  const apiCall = useAPICall();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('tmI');
  const campaignRecId = searchParams.get('cmI');
  const isTeamLeader = searchParams.get('tmL');
  //const viewPortType = useBreakpoint();
  //const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;

  const { userInfo } = useAppSelector(state => state.auth);
  const [teamData, setTeamData] = useState<TeamData>(initTeamForm);
  const [userData, setUserData] = useState(decodedData);
  const [individualData, setIndividualData] = useState<ParticipantDataStructure[]>();
  // const [displayCard, setDisplayCard] = useState(false);
  const [campaignId, setCampaignId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [dropdownValue, setDropdownValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [emailValid, setEmailValid] = useState(true);
  const [phoneLength, setPhoneLength] = useState(false);
  const [isConditionMet, setIsConditionMet] = useState(true);
  const [isTeamInactive, setIsTeamInactive] = useState(false);
  const [isTeamEnded, setIsTeamEnded] = useState(false);
  const [isDateValid, setIsDateValid] = useState(true);
  const [participantInvalid, setParticipantInvalid] = useState(true);
  const [orderNumberValid, setOrderNumberValid] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [verificationAlert, setVerificationAlert] = useState(false);
  const [invalidOrderMessage, setInvalidOrderMessage] = useState<string>('');

  // const [mobileDrawer, setMobileDrawer] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [check, setCheck] = useState(false);
  const [campaignData, setCampaignData] = useState<CampaignConfigDataOrder>();

  const [addParticipantData, setAddParticipantData] = useState<AddParticipantStructure>({
    campaignId: '',
    userId: 0,
    loginId: userInfo?.loginId ?? '',
    coachId: '',
    enrollmentDate: '',
    directLeaderName: '',
    pinLevel: '',
    orderNumber: '',
    directLeaderId: 0,
    directLeaderPinLevel: 0,
    endDate: '',
    remarks: '',
    invisibleParticipant: {
      name: '',
      email: '',
      phone: '',
    },
    isNewAbo: false,
  });

  const [orderData, setOrderData] = useState<OrderData>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Call the campaignDetails API to get the campaignId
        const campaignDetailsResponse = await apiCall(CampaignService.getCampaignDetail, `${campaignRecId}`);
        // Extract campaignId from the response
        const campaignIdName = campaignDetailsResponse?.data?.data?.campaignId;
        setCampaignData(campaignDetailsResponse?.data?.data);
        setCampaignId(campaignIdName);
        setCampaignName(campaignDetailsResponse?.data?.data?.campaignName);

        const isDateValid = campaignDetailsResponse?.data?.data?.registerEnd
          ? new Date() <= new Date(convertGMT8date(campaignDetailsResponse?.data?.data?.registerEnd))
          : true;

        setIsDateValid(isDateValid);

        // Now, if campaignId is available, call ParticipantService.getTeamParticipants
        if (campaignIdName) {
          const response = await apiCall(ParticipantService.getTeamParticipants, {
            campaignId: campaignIdName,
            teamId: teamId,
          });
          setCampaignId(campaignIdName);
          setIndividualData(response?.data?.data ?? []);
        }
      } catch (error) {
        LoggerService.log('Failed to retrieve user details', error);
      }

      // Call selectedUser and trackPage
      const timeout = setTimeout(() => {
        trackPage(PAGES.registerForm);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    };

    fetchData();
  }, [campaignRecId]);

  useEffect(() => {
    (async () => {
      try {
        const response = await apiCall(TeamService.getTeamDetails, `${teamId}`);
        if (response?.data?.status === false || !response?.data?.data) {
          //navigate('/error-page/no-result-found');
        } else {
          const teamData = response?.data?.data ?? initTeamForm;
          setTeamData(teamData);

          // if (teamData.campaignId) {
          //   const campaignResult = await apiCall(
          //     CampaignService.getCampaignDetailByCampaignId,
          //     `${teamData.campaignId}`,
          //   );
          //   const campaignData = campaignResult?.data?.data ?? {};
          //   setCampaignData(campaignData);
          // }
        }
      } catch (error) {
        LoggerService.log('Failed to retrieve user details', error);
      }
    })();
  }, [teamId]);

  // useEffect(() => {
  //   (async () => {
  //     if (userInfo?.loginId && campaignData?.configJson) {
  //       const result = await apiCall(MDMSService.getMDMSUserData, {
  //         aboNumber: userInfo?.loginId,
  //         numOfDays: campaignData?.configJson?.activities?.participant?.newAbo?.numOfDays ?? 365,
  //       });
  //       const mdmsListData = result?.data?.data;
  //       if (Object.keys(mdmsListData).length > 0) {
  //         setAddParticipantData(prevValue => ({ ...prevValue, isNewAbo: mdmsListData.isNewAbo }));
  //       }
  //     }
  //   })();
  // }, [userInfo?.loginId]);

  const { phone } = userData;
  let countryCode: string, number: string;
  if (phone) [countryCode, number] = phone.split(' ');

  useEffect(() => {
    if (userData) {
      if (phone) {
        setDropdownValue(countryCode);
        setPhoneValue(number);
      }
    }
    if (!userData?.phone || userData?.phone === '') {
      setPhoneLength(true);
    }
    if (!userData?.email || userData?.email === '') {
      setEmailValid(false);
    }
    validateParticipant();
  }, [userData]);

  useEffect(() => {
    if (individualData && teamData.teamId) {
      validateParticipant();
    }
  }, [addParticipantData, individualData, teamData]);

  const fetchProfileImage = async () => {
    try {
      const response = await AttachmentService.downloadProfileImage(decodedData?.userId.toString() ?? '');
      if (response.data && response.data.data && response.data.data.image) {
        setProfileImageUrl(response.data.data.image);
      }
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  useEffect(() => {
    fetchProfileImage();
  }, [decodedData]);

  const validateParticipant = () => {
    const { orderNumber } = addParticipantData ?? {};
    const { email, phone } = userData ?? {};

    const isAnyFieldEmpty = !orderNumber || !number || !email || !phone;
    const checkLength = individualData?.filter(participant => participant?.status === 'ACTIVE')?.length ?? 0;
    const isLengthValid = checkLength < Number(teamData?.teamSize);

    setIsConditionMet(isLengthValid);
    setParticipantInvalid(!isLengthValid || isAnyFieldEmpty || !emailValid || phoneLength);
    setIsTeamInactive(teamData?.isDeleted || teamData?.status === 'INACTIVE');
    setIsTeamEnded(new Date(convertGMT8date(teamData?.teamEndDate)) <= new Date());

  };

  const onSave = async () => {
    if (participantInvalid) {
      const checkLength = individualData?.filter(participant => participant?.status === 'ACTIVE')?.length ?? 0;
      const isLengthValid = checkLength < Number(teamData?.teamSize);
      if (!isLengthValid) {
        adminToast({
          message: 'Team has reached its active participant limit.',
          theme: Theme.ERROR,
        });
        return;
      } else {
        adminToast({
          message: 'Please enter the valid information and all mandatory field.',
          theme: Theme.ERROR,
        });
        return;
      }
    }

    let defaultOrder = {
      orderStatus: orderData?.success ? 1 : 0,
      errorMessage: orderData?.errorMessage ?? '',
      orderNumber: orderData?.orderNumber ?? '',
      orderName: orderData?.orderName ?? '',
      orderAmount: orderData?.orderAmount ?? null,
    };

    let isNewAbo = false;
    let highestPinLevel;
    let pin;
    if (userInfo?.loginId && campaignData?.configJson) {
      const result = await apiCall(MDMSService.getMDMSUserData, {
        aboNumber: userInfo?.loginId,
        numOfDays: campaignData?.configJson?.activities?.participant?.newAbo?.numOfDays ?? 365,
      });
      //console.log(result);
      isNewAbo = result?.data?.data.isNewAbo;
      highestPinLevel = result?.data?.data.highestPinLevel;
      pin = result?.data?.data.pin;
      // if (Object.keys(mdmsListData).length > 0) {
      //   setAddParticipantData(prevValue => ({ ...prevValue, isNewAbo: mdmsListData.isNewAbo }));
      // }
    } else {
      LoggerService.log('Failed to retrieve new abo details');
    }

    let combinedRemark = `${addParticipantData?.remarks} ${defaultOrder.errorMessage}`;

    const payload = {
      campaignId: campaignId,
      campaignName: campaignName,
      teamId: teamData.teamId,
      teamName: teamData.isIndividualTeam !== 1 ? teamData.teamName : userData?.name ,
      isIndividualTeam: teamData.isIndividualTeam,
      name: userData?.name,
      email: userData?.email,
      phone: userData?.phone,
      loginId: userInfo?.loginId,
      coachId: teamData.coachId,
      orderNumber: addParticipantData?.orderNumber,
      orderAmount: defaultOrder.orderAmount,
      orderStatus: defaultOrder.orderStatus, // validation fail give 0/1
      orderName: defaultOrder.orderName,
      remarks: combinedRemark,
      directLeaderId: userData?.directLeaderId,
      directLeaderName: userData?.directLeaderName,
      directLeaderPinLevel: userData?.userPinName,
      invisibleParticipant: {
        name: addParticipantData.invisibleParticipant.name,
        email: addParticipantData.invisibleParticipant.email,
        phone: addParticipantData.invisibleParticipant.phone,
      },
      enrollmentDate: new Date().toISOString().slice(0, -1),
      endDate: new Date().toISOString().slice(0, -1),
      isTeamLeader: isTeamLeader === '1' ? 'true' : 'false',
      isNewAbo: isNewAbo,
      highestPinLevel: highestPinLevel,
      pinLevel: pin,
      campaignConsent: check,
    };
    // try {
    const result = await apiCall(ParticipantService.addParticipant, payload);

    if (!result?.data?.result) {
      if (result.status !== 500) {
        adminToast({
          message: `${result?.data?.message}`,
          theme: Theme.ERROR,
        });
      }
    } else {
      await removeState(['source', 'team']);
      navigate(`/`);
      window.location.reload();
      adminToast({
        message: `Thank you for joining ${teamData?.campaignName}.`,
        theme: Theme.SUCCESS,
      });
    }
  };

  const handleInputChange = (name: string, value: string) => {
    if (name === 'orderNumber') {
      const regex = /^[0-9,]*$/;

      if (!regex.test(value)) {
        return;
      }
    }

    // Split the input by commas and trim each part
    const orders = value.split(',').map(order => order.trim());

    // Limit order numbers
    if (orders.length > (campaignData?.activities?.maxOrderNumber ?? 3)) {
      orders.splice(campaignData?.activities?.maxOrderNumber ?? 3);
      value = orders.join(',');
    }
    // Split the name into an array of keys
    const keys = name.split('.');

    setAddParticipantData((prevData: DynamicObjectInterface) => {
      let newData = { ...prevData };

      let currentLevel = newData;
      for (let i = 0; i < keys?.length - 1; i++) {
        if (!currentLevel[keys[i]]) {
          currentLevel[keys[i]] = {};
        }
        currentLevel = currentLevel[keys[i]];
      }

      // Update the value at the nested key
      currentLevel[keys[keys?.length - 1]] = value;

      return newData;
    });

    validateParticipant();
  };

  const handleUserInputChange = (name: string, value: string) => {
    setUserData((prevUserData: DynamicObjectInterface) => ({
      ...prevUserData,
      [name]: value,
    }));
    if (name === 'email') {
      setEmailValid(isValidEmail(value));
    }
  };

  const handleVerifyOrder = async () => {
    setVerificationAttempted(true);
    setVerificationAlert(false);
    const payload = {
      campaignId: teamData?.campaignId,
      aboId: addParticipantData?.loginId || userInfo?.loginId,
      salesOrderNumber: addParticipantData?.orderNumber,
    };

    if (addParticipantData.orderNumber !== '') {
      try {
        const response = await apiCall(ValidationService.validateOrderNumber, payload);
        const result: OrderData = response.data[0];
        setOrderData(result);
        if (result.success) {
          setOrderNumberValid(true);
          setInvalidOrderMessage('The order number(s) provided are valid');
        } else {
          setOrderNumberValid(false);
          setInvalidOrderMessage(
            'The order number(s) provided above will need to be verified by Amway. We will reach out if additional information is needed.',
          );
        }
      } catch (error) {
        LoggerService.log('Error verifying order:', error);
        setOrderNumberValid(false);
        setInvalidOrderMessage('An error occurred while verifying the order number. Please try again later.');
      }
    } else {
      setOrderNumberValid(false);
    }
  };

  // const buttonConfig = [
  //   {
  //     title: t('admin.addParticipant.register'),
  //     buttonStyle: ButtonStyles.SOLID,
  //     theme: Theme.PURPLE as ButtonTheme,
  //     disabled: participantInvalid,
  //     onClick: onSave,
  //   },
  // ];

  //-------------------upload image----------------------------------
  const deleteProfile = async () => {
    const payload = {
      loginId: decodedData?.userId,
    };
    const deleteProfileResponse = await apiCall(AttachmentService.removeProfile, payload);

    if (deleteProfileResponse.status === 200) {
      setProfileImageUrl('');
    }
  };

  const handleFileImage = (e: customEventPropType, view: string) => {
    const file = e.files[0];
    setProfileImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setProfileImageUrl(objectUrl);
    setShowModal(false);
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0] as File | null;

    setProfileImageFile(file);
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setProfileImageUrl(objectUrl);
    } else {
      setProfileImageUrl('');
    }
    setShowModal(false);
  };

  useEffect(() => {
    if (profileImageFile !== null) {
      handleFileUpload();
    }
  }, [profileImageFile]);

  const handleFileUpload = async () => {
    try {
      if (profileImageFile && String(decodedData?.userId)) {
        const formData = processProfilePictureData(profileImageFile, String(decodedData?.userId));
        const response = await apiCall(() => AttachmentService.postUploadProfile(formData));

        if (response.status === 200) {
          LoggerService.log('Successfully upload image', response);
        }
      }
    } catch (error) {
      LoggerService.log('Error upload image', error);
    }
  };

  const uploadImageModal = (
    <>
      {showModal && (
        <Modal
          primaryButtonLabel={''}
          headline={'Upload your Profile Picture'}
          containerWidth={'570px'}
          containerHeight={'570px'}
          closeIcon={true}
          onClose={() => setShowModal(false)}
        >
          <div>
            {profileImageUrl ? (
              <div className={styles['profile-image-container']}>
                <div className={styles['image-wrapper']}>
                  <img
                    src={profileImageUrl}
                    alt="Before Front View"
                    className={styles['profile-image']}
                    // style={{ borderRadius: '50%' }}
                  />
                </div>
                <div className={styles['buttons-container']}>
                  <button
                    className={styles['change-photo']}
                    onClick={() => document.getElementById('profileImageUrl')?.click()}
                  >
                    <img src={ChangeButton} alt="bg" />
                    <input
                      type="file"
                      id="profileImageUrl"
                      accept="image/*"
                      onChange={e => handleFile(e, 'profileImageUrl')}
                      style={{ display: 'none' }}
                    />
                  </button>
                  <button className={styles['remove-photo']} onClick={() => deleteProfile()}>
                    <img src={RemoveButton} alt="bg" />
                  </button>
                </div>
              </div>
            ) : (
              <ImageEditor onFileUpload={e => handleFileImage(e, 'file')} accept="image/*" />
            )}
          </div>
        </Modal>
      )}
    </>
  );

  const showModalFn = () => {
    setShowModal(true);
    // if (isMobile) {
    //   setMobileDrawer(false);
    // }
  };

  return (
    <div className={styles['join-team-container']}>
      <div className={styles['card']}>
        <div className={styles['card-header']}>
          <h2>Participant Registration Form</h2>
        </div>
        {isTeamInactive ? (
          <Tag
            text="Team is inactive or has been deleted. Contact the admin if you have trouble joining."
            theme={Theme.ERROR}
            style={TagStyle.OUTLINE}
          />
        ) : isTeamEnded ? (
          <Tag
            text="Team has ended. Contact the admin if you have trouble joining."
            theme={Theme.ERROR}
            style={TagStyle.OUTLINE}
          />
        ) : !isConditionMet ? (
          <Tag
            text="Team has reached its active participant limit. Contact the admin if you have trouble joining."
            theme={Theme.ERROR}
            style={TagStyle.OUTLINE}
          />
        ) : !isDateValid ? (
          <Tag
            text="This challenge is no longer open for registration. Contact the admin if you have trouble joining."
            theme={Theme.ERROR}
            style={TagStyle.OUTLINE}
          />
        ) : null}
        {/* {isDesktopView ? (
        <AdminPageHeader title={t('Participant Registration Form')} button={buttonConfig} />
      ) : (
        <DashboardPageHeaderMobile title={t('Participant Registration Form')} />
      )} */}
        <div className={styles['participant-container']}>
          <div className={styles['add-participant-form-container-three-column']}>
            <div className={`${styles['card-user-img']}`}>
              <div className={styles['img-container']}>
                <img
                  src={profileImageUrl ? profileImageUrl : personalizeImg}
                  alt="personalize"
                  onClick={showModalFn}
                  className={styles['main-profile-image']}
                />
              </div>
            </div>
            <div></div>
            <div></div>
            <div className={styles['input-container']}>
              <div className={styles['pin-dropdown']}>
                <Input
                  name={'campaignName'}
                  placeholder={t('admin.addParticipant.campaignTitle')}
                  value={teamData.campaignName}
                  disabled
                  mlpVersion={MLPVersion.ONE}
                  label={t('admin.addParticipant.campaignTitle')}
                />
              </div>
            </div>
            <div className={styles['input-container']}>
              {isTeamLeader !== '3' && (
                <div className={styles['team-name-section']}>
                  <Input
                    name={'teamName'}
                    placeholder={'Team Name'}
                    value={teamData.teamName}
                    disabled
                    mlpVersion={MLPVersion.ONE}
                    label={'Team  Name'}
                  />
                </div>
              )}
            </div>
            {/* <div className={styles['input-container']}>
            <TextArea
              mlpVersion={MLPVersion.ONE}
              label={'Description'}
              placeholder={'Description'}
              value={teamData?.description}
              disabled
            />
          </div> */}
          </div>
          <hr className={styles['horizontal-line']} />
          <div className={styles['add-participant-form-container-three-column']}>
            <div>
              <Input
                name={'aboId'}
                placeholder={'ABO ID'}
                value={userInfo?.loginId ?? ''}
                disabled
                mlpVersion={MLPVersion.ONE}
                label={'ABO / APC No.'}
              />
            </div>
            <div className={styles['participant-section']}>
              <Input
                label="Participant Name"
                name="name"
                placeholder={`Participant Name`}
                mlpVersion={MLPVersion.ONE}
                value={decodedData?.name}
                required={true}
                disabled
              />
            </div>
            <div className={styles['participant-section']}>
              <Input
                name="email"
                label={`Participant Email`}
                value={userData?.email}
                mlpVersion={MLPVersion.ONE}
                required={true}
                onChange={e => {
                  handleUserInputChange('email', e.target.value);
                }}
                status={
                  userData?.email.length === 0
                    ? InputValidations.INVALID
                    : !emailValid
                      ? InputValidations.INVALID
                      : InputValidations.DEFAULT
                }
                message={
                  userData?.email.length === 0
                    ? `Email is required`
                    : emailValid
                      ? ''
                      : `Please enter a valid email address`
                }
                disabled={!isConditionMet || isTeamInactive || !isDateValid}
              />
            </div>
            <div className={styles['participant-section']}>
              <div className={styles['label-text-phone']}>
                {'Participant Phone Number '}
                <span className={styles['label-red']}>*</span>
              </div>
              <PhoneNumber
                name={'phoneNumber'}
                data={countryCodeNumber}
                inputValue={phoneValue}
                dropdownValue={dropdownValue}
                required={true}
                placeholder={'Participant Phone Number'}
                status={
                  phoneValue.length === 0
                    ? InputValidations.INVALID
                    : phoneLength
                      ? InputValidations.INVALID
                      : InputValidations.DEFAULT
                }
                message={
                  phoneValue.length === 0 ? `Phone number is required` : phoneLength ? `Invalid Phone Number` : ''
                }
                configData={{
                  label: 'name',
                  onSelect: (e: DropDownData) => {
                    setDropdownValue(e.name as string);
                    // Check if selectedOption is not empty before setting phoneValue
                    if (e.name) {
                      const combinedValue = `${e.name} ${phoneValue}`;
                      setPhoneValue(phoneValue === '' ? '' : phoneValue); // Ensure phoneValue is not set if it's already empty
                      handleUserInputChange('phone', combinedValue);
                    } else {
                      setPhoneValue(''); // Reset phoneValue to empty if dropdownValue is not selected
                      handleUserInputChange('phone', ''); // Update the state accordingly
                    }
                  },
                }}
                onInputChange={val => {
                  const phoneNumber = val ?? '';
                  setPhoneValue(phoneNumber);
                  setPhoneLength(phoneNumber.length > 0 && (phoneNumber.length < 8 || phoneNumber.length > 10));
                  if (phoneNumber) {
                    const combinedValue = `${dropdownValue} ${phoneNumber}`;
                    setPhoneValue(phoneNumber === '' ? '' : phoneNumber);
                    handleUserInputChange('phone', combinedValue);
                  } else {
                    setPhoneValue(''); // Reset phoneValue to empty if dropdownValue is not selected
                    handleUserInputChange('phone', ''); // Update the state accordingly
                  }
                }}
                disabled={!isConditionMet || isTeamInactive || !isDateValid}
                dropdownDisabled={!isConditionMet || isTeamInactive || !isDateValid}
              />
            </div>
            <div className={styles['participant-section']}>
              <Input
                name="orderNumber"
                label={`${t('Order Number')}`}
                value={addParticipantData?.orderNumber}
                required={true}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChange('orderNumber', e.target.value.toString());
                  if (verificationAttempted) setVerificationAttempted(false);
                }}
                message={
                  addParticipantData?.orderNumber !== ''
                    ? verificationAttempted
                      ? orderNumberValid
                        ? `Valid Order Number(s)`
                        : invalidOrderMessage
                      : verificationAlert
                        ? `*Please click the 'Verify Order' button to continue.`
                        : `*Enter up to ${campaignData?.activities?.maxOrderNumber ?? 3} order numbers, in full and separated by commas (e.g. "50004212345, 50004267890, 50004211223").`
                    : `*Enter up to ${campaignData?.activities?.maxOrderNumber ?? 3} order numbers, in full and separated by commas (e.g. "50004212345, 50004267890, 50004211223").`
                }
                status={
                  addParticipantData?.orderNumber !== ''
                    ? verificationAttempted
                      ? orderNumberValid
                        ? InputValidations.VALID
                        : InputValidations.WARNING
                      : verificationAlert
                        ? InputValidations.INVALID  
                        : InputValidations.DEFAULT
                    : InputValidations.DEFAULT
                }
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleVerifyOrder();
                  }
                }}
                disabled={!isConditionMet || isTeamInactive || !isDateValid}
                onBlur={() => {
                  addParticipantData?.orderNumber !== '' && !verificationAttempted && setVerificationAlert(true);
                }}
              />
            </div>
            <Button
              className={styles['verify-order']}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              title="Verify Order"
              disabled={addParticipantData?.orderNumber === ''}
              onClick={handleVerifyOrder}
            />
          </div>
          {/* {displayCard ? (
          <div className={styles['participant-details-container']}>
            <div className={styles['participant-details-title']}>Participant Details</div>
            <div className={styles['add-participant-form-container-three-column']}>
              <Label title={'Pin Level'} position={Position.HORIZONTAL} value={userData?.pinLevel ?? ''} />
              <Label
                title={'Upline ID'}
                position={Position.HORIZONTAL}
                value={userData?.directLeaderId?.toString() ?? ''}
              />
              <Label title={'Upline Name'} position={Position.HORIZONTAL} value={userData?.directLeaderName ?? ''} />
              <Label title={'Upline Pin'} position={Position.HORIZONTAL} value={userData?.directLeaderPinLevel ?? ''} />
              <Label position={Position.HORIZONTAL} title={'Email'} value={userData?.email ?? ''} />
              <Label position={Position.HORIZONTAL} title={'Phone Number'} value={userData?.phone ?? ''} />
            </div>
          </div>
        ) : null} */}

          {/* {!isDesktopView ? ( */}
          <Checkbox
            text={
              <p
                id="agree-checkbox"
                dangerouslySetInnerHTML={{
                  __html: t('securityPrivacy.agreementCampaign').replace(
                    '{terms-conditions}',
                    `<a href="/terms-and-conditions?cid=${campaignId}" target="_blank">${t('securityPrivacy.terms-condition')}</a>`,
                  ),
                }}
              />
            }
            onChange={() => setCheck(prevValue => !prevValue)}
            checked={check}
            className={styles['security-privacy-checkbox']}
            disabled={!isConditionMet || isTeamInactive || !isDateValid}
          />
          <div className={styles['participant-register-button']}>
            <Button
              buttonStyle={ButtonStyles.OUTLINE}
              title="Cancel"
              onClick={() => {
                window.location.href = '/';
              }}
            />
            <Button
              title={t('admin.addParticipant.register')}
              buttonStyle={ButtonStyles.SOLID}
              disabled={!check || !verificationAttempted || !addParticipantData?.orderNumber}
              onClick={onSave}
            />
          </div>
          {/* ) : null} */}
        </div>
        {uploadImageModal}
      </div>
    </div>
  );
};
